import React, { useState, useEffect } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  IconButton, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';
import { notify } from '../notifications';

interface Task {
  id: number;
  title: string;
  description: string;
  frequency: 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
}

const TaskScheduler: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [availableFiles, setAvailableFiles] = useState<string[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [newTask, setNewTask] = useState<Omit<Task, 'id'>>({
    title: '',
    description: '',
    frequency: 'DAILY'
  });

  const fetchTasks = async () => {
    try {
      const response = await axios.get('http://192.168.0.54:3002/api/tasks');
      if (response.data.success) {
        setTasks(response.data.tasks);
      }
    } catch (error) {
      console.error('Ошибка при получении задач:', error);
    }
  };

  const fetchAvailableFiles = async () => {
    try {
      const response = await axios.get('http://192.168.0.54:3002/api/available-files');
      if (response.data.success) {
        setAvailableFiles(response.data.files);
      }
    } catch (error) {
      console.error('Ошибка при получении списка файлов:', error);
    }
  };

  useEffect(() => {
    document.title = 'Планировщик | Zerde';
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchAvailableFiles();
  }, []);

  const handleSubmit = async () => {
    try {
      if (editingTask) {
        // Обновление существующей задачи
        const response = await axios.put(`http://192.168.0.54:3002/api/tasks/${editingTask.id}`, newTask);
        if (response.data.success) {
          setEditingTask(null);
        }
      } else {
        // Создание новой задачи
        const response = await axios.post('http://192.168.0.54:3002/api/tasks', newTask);
        if (!response.data.success) {
          throw new Error(response.data.message);
        }
      }

      fetchTasks();
      fetchAvailableFiles();
      setOpenDialog(false);
      setNewTask({
        title: '',
        description: '',
        frequency: 'DAILY'
      });
    } catch (error) {
      console.error('Ошибка при сохранении задачи:', error);
      notify.error('Произошла ошибка при сохранении задачи');
    }
  };

  const handleEdit = (task: Task) => {
    setEditingTask(task);
    setNewTask({
      title: task.title,
      description: task.description,
      frequency: task.frequency
    });
    setOpenDialog(true);
  };

  const handleDelete = async (taskId: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту задачу?')) {
      return;
    }

    try {
      const response = await axios.delete(`http://192.168.0.54:3002/api/tasks/${taskId}`);
      if (response.data.success) {
        fetchTasks();
        fetchAvailableFiles();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Ошибка при удалении задачи:', error);
      notify.error('Произошла ошибка при удалении задачи');
    }
  };

  const handleClose = () => {
    setOpenDialog(false);
    setEditingTask(null);
    setNewTask({
      title: '',
      description: '',
      frequency: 'DAILY'
    });
  };

  return (
    <Box>
      <Button
        variant="contained"
        color="primary"
        onClick={() => setOpenDialog(true)}
        sx={{ position: 'absolute', bottom: 15, right: 15, fontSize: '2rem', padding: 0, borderRadius: '50%' }}
      >
        +
      </Button>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>Название файла</TableCell>
              <TableCell>Периодичность</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task) => (
              <TableRow key={task.id}>
                <TableCell>{task.title}</TableCell>
                <TableCell>{task.description}</TableCell>
                <TableCell>{task.frequency}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Редактировать">
                    <IconButton onClick={() => handleEdit(task)} size="small">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Удалить">
                    <IconButton onClick={() => handleDelete(task.id)} size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTask ? 'Редактировать задачу' : 'Добавить новую задачу'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Название"
            value={newTask.title}
            onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Файл обработки</InputLabel>
            <Select
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              label="Файл обработки"
            >
              {availableFiles.map((file) => (
                <MenuItem key={file} value={file}>
                  {file}
                </MenuItem>
              ))}
              {/* Показываем текущий файл при редактировании, даже если он уже использован */}
              {editingTask && !availableFiles.includes(editingTask.description) && (
                <MenuItem value={editingTask.description}>
                  {editingTask.description}
                </MenuItem>
              )}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Периодичность</InputLabel>
            <Select
              value={newTask.frequency}
              onChange={(e) => setNewTask({ ...newTask, frequency: e.target.value as Task['frequency'] })}
              label="Периодичность"
            >
              <MenuItem value="10_MINUTES">10 минут</MenuItem>
              <MenuItem value="HOURLY">Каждый час</MenuItem>
              <MenuItem value="DAILY">Ежедневно</MenuItem>
              <MenuItem value="WEEKLY">Еженедельно</MenuItem>
              <MenuItem value="MONTHLY">Ежемесячно</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingTask ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskScheduler;